<!--
	03 / THE MATH — the pattern-library dependency calculator. The one place on the
	page the visitor operates something, and it argues with their own numbers rather
	than ours, which is why it earns its space in a scroll that's otherwise watched.

	Progressive enhancement: the initial state renders real numbers into prerendered
	HTML (the whole argument is readable with no JS); JS only makes the sliders live.

	The panel it reports into is the app's own dark surface — the page asks the
	question on paper, the instrument answers. Same grammar as every other window
	on the site.
-->
<script lang="ts">
	import { Section, SectionHead, Button, Readout } from '$lib/components/ui';
	import { calculator } from '$lib/content/calculator';
	import { usd, pricingCard } from '$lib/content/pricing';
	import { SprungNumber } from '$lib/motion/sprung.svelte';

	const { inputs } = calculator;
	// Explicitly `number`: calculator.ts is `as const`, so the defaults infer as the
	// literal types 200/4/650 and the sliders couldn't assign anything else.
	let subscription = $state<number>(inputs.subscription.default);
	let missedJobs = $state<number>(inputs.missedJobs.default);
	let jobValue = $state<number>(inputs.jobValue.default);

	const libraryYear = $derived(subscription * 12);
	const lostYear = $derived(missedJobs * 12 * jobValue);
	const totalYear = $derived(libraryYear + lostYear);

	// The displayed values spring to the visitor's numbers instead of snapping —
	// the instrument settles like a real needle. SSR renders the exact defaults;
	// the spring only ever runs between two true values, on the visitor's input.
	const library = new SprungNumber(inputs.subscription.default * 12);
	const lost = new SprungNumber(inputs.missedJobs.default * 12 * inputs.jobValue.default);
	const total = new SprungNumber(library.current + lost.current);
	$effect(() => {
		library.set(libraryYear);
		lost.set(lostYear);
		total.set(totalYear);
	});

	// The split bar shares the sprung values, so the bar and the figures move as
	// one instrument. Guard: all sliders at zero means no split to draw.
	const splitLibrary = $derived(
		total.current > 0 ? Math.round((library.current / total.current) * 1000) / 10 : 50
	);

	interface SliderCfg {
		label: string;
		suffix: string;
		min: number;
		max: number;
		step: number;
	}
</script>

{#snippet slider(cfg: SliderCfg, value: number, set: (v: number) => void, display: string)}
	<div class="slider" style:--pct={(value - cfg.min) / (cfg.max - cfg.min)}>
		<div class="slider__head">
			<span class="slider__label">{cfg.label}</span>
			<span class="slider__val">
				{display}
				<span class="slider__suffix">{cfg.suffix}</span>
			</span>
		</div>
		<input
			type="range"
			class="range"
			min={cfg.min}
			max={cfg.max}
			step={cfg.step}
			{value}
			oninput={(e) => set(e.currentTarget.valueAsNumber)}
			aria-label={cfg.label}
		/>
	</div>
{/snippet}

<Section id="math" tone="panel">
	<SectionHead
		index={calculator.index}
		label={calculator.label}
		heading={calculator.heading}
		intro={calculator.intro}
	/>

	<div class="calc">
		<!-- Inputs -->
		<div class="calc__inputs">
			{@render slider(
				inputs.subscription,
				subscription,
				(v) => (subscription = v),
				usd(subscription)
			)}
			{@render slider(inputs.missedJobs, missedJobs, (v) => (missedJobs = v), String(missedJobs))}
			{@render slider(inputs.jobValue, jobValue, (v) => (jobValue = v), usd(jobValue))}
			<p class="calc__disclaimer">{calculator.disclaimer}</p>
		</div>

		<!-- Output -->
		<div class="calc__out app-surface">
			<div class="calc__splits">
				<Readout
					label={calculator.outputs.library}
					value={usd(Math.round(library.current))}
					size="sm"
				/>
				<Readout
					label={calculator.outputs.lost}
					value={usd(Math.round(lost.current))}
					size="sm"
					accent
				/>
			</div>

			<!-- The split, drawn: how much of the bleed is the subscription itself
			     versus the work it can't take. Shares the sprung values, so the bar
			     and the figures settle together. The numbers sit in the Readouts. -->
			<div class="split" aria-hidden="true">
				<div class="split__seg split__seg--library" style:width="{splitLibrary}%"></div>
				<div class="split__seg split__seg--lost" style:width="{100 - splitLibrary}%"></div>
			</div>

			<div class="calc__total">
				<p class="calc__total-label">{calculator.outputs.total}</p>
				<p class="calc__total-val">{usd(Math.round(total.current))}</p>
			</div>

			<p class="calc__punch">{calculator.punchline}</p>
			<Button href="/#pricing" class="calc__cta">{pricingCard.cta}</Button>
		</div>
	</div>
</Section>

<style>
	.calc {
		display: grid;
		gap: clamp(2rem, 4vw, 3rem);
		margin-top: clamp(3rem, 5vw, 4rem);
		align-items: start;
	}
	@media (min-width: 900px) {
		.calc {
			grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
			gap: 4rem;
		}
	}

	/* ── Inputs ─────────────────────────────────────────────────────────── */
	.calc__inputs {
		display: flex;
		flex-direction: column;
		gap: 2.25rem;
	}
	.slider__head {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: 1rem;
		margin-bottom: 0.75rem;
	}
	.slider__label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--color-ink-400);
	}
	.slider__val {
		font-family: var(--font-display);
		font-size: var(--text-h3);
		font-weight: 600;
		font-variant-numeric: tabular-nums;
		color: var(--color-ink-900);
		white-space: nowrap;
	}
	.slider__suffix {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		font-weight: 400;
		color: var(--color-ink-400);
	}

	/* The track fills to the thumb — a control that shows its own value, which is
	   what a real instrument does. `--pct` is set inline from the bound value. */
	.range {
		-webkit-appearance: none;
		appearance: none;
		width: 100%;
		height: 3px;
		border-radius: 2px;
		background-image: linear-gradient(
			to right,
			var(--color-cut-600) calc(var(--pct) * 100%),
			var(--color-line-strong) calc(var(--pct) * 100%)
		);
		cursor: pointer;
	}
	.range::-webkit-slider-thumb {
		-webkit-appearance: none;
		appearance: none;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background-color: var(--color-cut-400);
		border: 2px solid var(--color-ink-900);
		box-shadow: 0 1px 3px rgba(20, 20, 12, 0.25);
		cursor: pointer;
	}
	.range::-moz-range-thumb {
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background-color: var(--color-cut-400);
		border: 2px solid var(--color-ink-900);
		box-shadow: 0 1px 3px rgba(20, 20, 12, 0.25);
		cursor: pointer;
	}
	.range:focus-visible {
		outline: 2px solid var(--color-cut-700);
		outline-offset: 6px;
	}

	.calc__disclaimer {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.03em;
		color: var(--color-ink-400);
		max-width: 40ch;
	}

	/* ── Output ─────────────────────────────────────────────────────────── */
	.calc__out {
		padding: clamp(1.75rem, 3vw, 2.5rem);
		border-radius: var(--radius-win);
		box-shadow: var(--shadow-win);
	}
	.calc__splits {
		display: flex;
		flex-wrap: wrap;
		gap: 1.5rem 3rem;
	}

	/* ── The split bar ──────────────────────────────────────────────────── */
	.split {
		display: flex;
		gap: 3px;
		height: 6px;
		margin-top: 1.75rem;
		border-radius: 3px;
		overflow: hidden;
	}
	.split__seg {
		height: 100%;
		min-width: 3px;
	}
	.split__seg--library {
		background-color: var(--color-app-500);
	}
	.split__seg--lost {
		background-color: var(--color-cut-400);
	}
	.calc__total {
		margin-top: 2rem;
		padding-top: 2rem;
		border-top: 1px solid var(--color-app-700);
	}
	.calc__total-label {
		font-family: var(--font-mono);
		font-size: var(--text-mono-s);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-500);
	}
	.calc__total-val {
		margin-top: 0.5rem;
		font-family: var(--font-display);
		font-size: clamp(2.75rem, 6vw, 4.25rem);
		font-weight: 600;
		line-height: 1;
		letter-spacing: -0.035em;
		font-variant-numeric: tabular-nums;
		color: var(--color-cut-400);
	}
	.calc__punch {
		margin-top: 2rem;
		max-width: 34ch;
		font-family: var(--font-display);
		font-size: var(--text-h3);
		font-weight: 500;
		line-height: 1.35;
		letter-spacing: -0.015em;
		color: var(--color-bone-100);
		text-wrap: pretty;
	}
	.calc__out :global(.calc__cta) {
		margin-top: 2rem;
	}
</style>
